import { ChevronLeft, ChevronRight, Search } from "lucide-react"; // Import Search icon
import React, { useEffect, useState } from "react"; // Import useState
import { useNavigate } from "react-router-dom";

const StaffManagement = ({ staffMembers, userRole }) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0); // State for current page index
  const [searchTerm, setSearchTerm] = useState(""); // State for search term
  const itemsPerPage = 4; // Number of staff members per page

  // Filter staff members based on search term first
  const filteredStaff = staffMembers.filter((staff) =>
    staff.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const getStaffPath = () => {
    switch (userRole) {
      case "subAdmin":
        return "/subAdmin/staff";
      case "admin":
      default:
        return "/admin/staff";
    }
  };
  // Calculate the staff members to display for the current page
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedStaff = filteredStaff.slice(startIndex, endIndex); // Slice the filtered list

  const totalPages = Math.ceil(filteredStaff.length / itemsPerPage); // Calculate total pages based on filtered list

  const handleNextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages - 1));
  };

  const handlePrevPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 0));
  };

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm]);

  return (
    <div className="flex-1 bg-[#131313] rounded-lg h-full p-4 flex flex-col border-2 border-[#333333]">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
        <h3 className="text-xl font-medium">
          Staff Management ({filteredStaff.length}) {/* Show filtered count */}
        </h3>
        {/* Show total count */}
        <button
          className="text-[#FFCA20] flex items-center hover:underline"
          onClick={() => navigate(getStaffPath())}
        >
          Go to Staff Management <ChevronRight size={18} />
        </button>
      </div>
      <div className="mb-4">
        <div className="relative flex">
          <Search className="absolute left-3 top-2 pr-2 w-6 h-6 text-white" />
          <input
            type="text"
            placeholder="Search Staff"
            className="bg-transparent border border-[#333333] rounded-3xl text-white min-w-72 pl-10 pr-3 py-2 focus:outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {displayedStaff.length > 0 ? (
          displayedStaff.map(
            (
              staff // Map over displayedStaff
            ) => (
              <div
                key={staff._id} // Use a unique key like staff._id or staff.userId if available
                className="border-2 border-[#333333] rounded-lg p-4 flex flex-col items-center"
              >
                <div className="bg-gray-700 rounded-full w-16 h-16 mb-3 flex justify-center items-center overflow-hidden">
                  <img
                    src="https://www.w3schools.com/howto/img_avatar.png"
                    alt={staff.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="font-bold mb-3">{staff.name || "N/A"}</div>{" "}
                {/* Access populated name safely */}
                <div className="w-full text-sm text-gray-400 mb-1">
                  <div className="flex justify-between">
                    <span>Active Applications</span>
                    <span>{staff.loans?.length}</span>
                  </div>
                </div>
                <div className="w-full text-sm text-gray-400 mb-1">
                  <div className="flex justify-between">
                    <span>Approval Rate</span>
                    <span>{staff.approvalRate}%</span>
                  </div>
                </div>
                <div className="w-full text-sm text-gray-400">
                  <div className="flex justify-between">
                    <span>Avg Processing</span>
                    <span>{staff.avgProcessing}</span>
                  </div>
                </div>
              </div>
            )
          )
        ) : (
          <div className="col-span-1 sm:col-span-2 lg:col-span-4 text-center text-gray-400 py-5">
            No staff members found matching your search.
          </div>
        )}
      </div>
      <div className="flex justify-end items-center gap-2 mt-4">
        <button
          className={`bg-[#FFCA20] hover:bg-gray-700 rounded-full w-8 h-8 flex justify-center items-center text-black ${
            currentPage === 0 ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={handlePrevPage}
          disabled={currentPage === 0} // Disable if on the first page
        >
          <ChevronLeft size={18} />
        </button>

        <button
          className={`bg-[#FFCA20] hover:bg-gray-700 rounded-full w-8 h-8 flex justify-center items-center text-black ${
            currentPage >= totalPages - 1 ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={handleNextPage}
          disabled={currentPage >= totalPages - 1} // Disable if on the last page
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default StaffManagement;
